/**
 * ThemeManager - Handles theme switching and persistence
 * 
 * Use the shared `themeManager` singleton export for consistent behavior.
 * The manager can be configured once with a list of allowed themes.
 */

import { oemConfig } from './OEMConfig';
import { userSettingsService, SettingScope } from './UserSettingsService';

/**
 * Available DaisyUI themes
 */
const ALL_THEMES = [
  "light", "dark", "cupcake", "bumblebee", "emerald", "corporate", "synthwave", "retro", "cyberpunk",
  "valentine", "halloween", "garden", "forest", "aqua", "lofi", "pastel", "fantasy", "wireframe",
  "black", "luxury", "dracula", "cmyk", "autumn", "business", "acid", "lemonade", "night", "coffee", "winter",
  "dim", "nord", "sunset", "caramellatte", "abyss", "silk", "trisys"
] as const;

export type ThemeName = typeof ALL_THEMES[number];

export class ThemeManager {
  private static readonly STORAGE_KEY = 'theme';
  private static readonly DEFAULT_THEME: ThemeName = 'light';
  private forcedTheme: string | null = null;
  
  constructor(private readonly themes: readonly string[] = ALL_THEMES) {}

  /**
   * Get list of available themes
   */
  public getThemes(): readonly string[] {
    return this.themes;
  }

  /**
   * Initialize theme from storage or OEM forced mode
   */
  public init(): void {
    // Check OEM config for forced theme
    const themeConfig = oemConfig.getTheme();
    if (themeConfig.forcedMode) {
      this.forcedTheme = themeConfig.forcedMode;
      this.apply(themeConfig.forcedMode);
      return;
    }

    // Use stored theme or default
    const stored = this.getStored();
    if (stored && this.themes.includes(stored)) {
      this.apply(stored);
    } else {
      this.apply(ThemeManager.DEFAULT_THEME);
    }
  }

  /**
   * Apply a theme to the document
   */
  public apply(theme: string): void {
    // Apply the theme even if not in our restricted list (for flexibility)
    document.documentElement.setAttribute('data-theme', theme);
    this.store(theme);
    this.highlightActive(theme);
  }

  /**
   * Get currently active theme
   */
  public getCurrent(): string {
    return document.documentElement.getAttribute('data-theme') || ThemeManager.DEFAULT_THEME;
  }

  /**
   * Get stored theme from UserSettingsService
   */
  private getStored(): string | null {
    return userSettingsService.get<string>(ThemeManager.STORAGE_KEY) || null;
  }

  /**
   * Store theme in UserSettingsService
   */
  private store(theme: string): void {
    // Fire and forget - no need to await for UI theme switch
    userSettingsService.set(ThemeManager.STORAGE_KEY, theme, SettingScope.LOCAL).catch(err => {
      console.error('Failed to save theme preference', err);
    });
  }

  /**
   * Highlight the active theme in the UI
   */
  private highlightActive(theme: string): void {
    // Clear all active states
    document.querySelectorAll("[data-theme-item]").forEach((el) => {
      el.classList.remove("active");
      const check = el.querySelector(".theme-check") as SVGElement | null;
      if (check) check.style.opacity = "0";
    });

    // Set active state for current theme
    const btn = document.querySelector(`[data-theme-item="${theme}"]`) as HTMLElement | null;
    if (btn) {
      btn.classList.add("active");
      const check = btn.querySelector(".theme-check") as SVGElement | null;
      if (check) check.style.opacity = "1";
    }
  }
}

/**
 * Shared theme manager singleton instance
 * Use this for consistent theme management across the application
 */
export const themeManager = new ThemeManager();
